import { apiClient } from './client';
import type {
  ApiResponse,
  UserPublic,
  RegisterInput,
  LoginInput,
  AuthResult,
  RegisterResponse,
  SocialAuthInput,
  SocialAuthResult,
  VerifyEmailInput,
} from '@/types';

/**
 * Register a new user account
 */
export async function register(input: RegisterInput): Promise<ApiResponse<RegisterResponse>> {
  const response = await apiClient.post<ApiResponse<RegisterResponse>>('/auth/register', input);
  return response.data;
}

/**
 * Login with email and password
 */
export async function userLogin(input: LoginInput): Promise<ApiResponse<AuthResult>> {
  const response = await apiClient.post<ApiResponse<AuthResult>>('/auth/login', input);
  return response.data;
}

/**
 * Login/register with Google, Apple, or Facebook
 */
export async function socialLogin(input: SocialAuthInput): Promise<ApiResponse<SocialAuthResult>> {
  const response = await apiClient.post<ApiResponse<SocialAuthResult>>('/auth/social', input);
  return response.data;
}

/**
 * Logout current user session (stateless on backend)
 */
export async function userLogout(): Promise<ApiResponse<{ message: string }>> {
  const response = await apiClient.post<ApiResponse<{ message: string }>>('/auth/logout');
  return response.data;
}

/**
 * Verify email with a 6-digit code or legacy token
 */
export async function verifyEmail(input: VerifyEmailInput): Promise<ApiResponse<{ user: UserPublic; message: string }>> {
  const response = await apiClient.post<ApiResponse<{ user: UserPublic; message: string }>>('/auth/verify-email', input);
  return response.data;
}

/**
 * Resend verification email
 */
export async function resendVerificationEmail(email: string): Promise<ApiResponse<{ message: string }>> {
  const response = await apiClient.post<ApiResponse<{ message: string }>>('/auth/resend-verification', { email });
  return response.data;
}

/**
 * Request password reset
 */
export async function forgotPassword(email: string): Promise<ApiResponse<{ message: string }>> {
  const response = await apiClient.post<ApiResponse<{ message: string }>>('/auth/forgot-password', { email });
  return response.data;
}

/**
 * Reset password with token
 */
export async function resetPassword(token: string, password: string): Promise<ApiResponse<{ message: string }>> {
  const response = await apiClient.post<ApiResponse<{ message: string }>>('/auth/reset-password', { token, password });
  return response.data;
}

/**
 * Refresh access token
 */
export async function refreshToken(refreshToken: string): Promise<ApiResponse<{ token: string }>> {
  const response = await apiClient.post<ApiResponse<{ token: string }>>('/auth/refresh', { refreshToken });
  return response.data;
}

/**
 * Get current user profile
 */
export async function getCurrentUser(): Promise<ApiResponse<UserPublic>> {
  const response = await apiClient.get<ApiResponse<UserPublic>>('/auth/me');
  return response.data;
}
